import { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";
import "./common.css";
import QRCode from "qrcode";

// ── constants ──────────────────────────────────────────────────────────────
const CARD_NO      = "5425233430109903";
const CARD_DISPLAY = "5425 2334 3010 9903";
const CORRECT_PIN  = "1246";
const INIT_BALANCE = 25000;
const PIN_TIMEOUT_MS = 60_000; // 1 minute

// Change this to your Flask backend URL
const API_BASE = "https://atmsim-pip.onrender.com/api";

const INITIAL_TXN = [
  { id: 1, type: "credit", desc: "Salary Credit",    amount: 50000, date: "01 Apr" },
  { id: 2, type: "debit",  desc: "Grocery Store",    amount: 1200,  date: "05 Apr" },
  { id: 3, type: "debit",  desc: "Electricity Bill", amount: 850,   date: "08 Apr" },
  { id: 4, type: "credit", desc: "Refund Received",  amount: 300,   date: "10 Apr" },
  { id: 5, type: "debit",  desc: "Online Shopping",  amount: 2300,  date: "14 Apr" },
];

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);


//removed link redirect for optimization


// ── Load face-api models ONCE when the module loads ────────────────────────
// Models must be in your /public/models/ folder.
let faceModelsLoaded = false;
async function loadFaceModels() {
  if (faceModelsLoaded) return;
  try {
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
      faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
      faceapi.nets.faceRecognitionNet.loadFromUri("/models"),
    ]);
    faceModelsLoaded = true;
    console.log("face-api models loaded ✓");
  } catch (err) {
    console.error("Failed to load face-api models:", err);
  }
}

// ── Cash Note component ────────────────────────────────────────────────────
function CashNote({ amount, delay, left }) {
  const [pos, setPos]         = useState(-12);
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    const t1 = setTimeout(() => setPos(50), 50);
    const t2 = setTimeout(() => setOpacity(0), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="cash-note" style={{
      left, bottom: pos, opacity,
      transition: `bottom ${0.9 + delay}s cubic-bezier(0.4,1.4,0.6,1), opacity 0.3s`,
      transitionDelay: `${delay}s, ${delay + 0.9}s`,
    }}>
      {fmtINR(amount)}
    </div>
  );
}

// ── Blinking cursor ────────────────────────────────────────────────────────
function Cursor() {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setShow(s => !s), 530);
    return () => clearInterval(t);
  }, []);
  return <span className="cursor" style={{ background: show ? "#00ff88" : "transparent" }} />;
}

// ── Welcome screen ─────────────────────────────────────────────────────────
function WelcomeScreen() {
  const [blink, setBlink] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setBlink(b => !b), 800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="screen-inner">
      <div className="bank-name">NAPPS BANK</div>
      <div className="bank-sub">24 / 7  BANKING  SERVICE</div>
      <hr className="screen-divider" />
      <div className="screen-prompt" style={{ marginTop: 8 }}>Welcome! Insert your card</div>
      <div className="welcome-blink" style={{ opacity: blink ? 1 : 0 }}>▶ TAP ANY KEY TO BEGIN</div>
      <div className="welcome-hint">Demo: Card {CARD_DISPLAY} | PIN {CORRECT_PIN}</div>
    </div>
  );
}

// ── Card entry screen ──────────────────────────────────────────────────────
function CardEntryScreen({ input, error }) {
  const chunks = (input.match(/.{1,4}/g) || []).join(" ");
  return (
    <div className="screen-inner">
      <div className="screen-title">CARD NUMBER</div>
      <div className="screen-msg" style={{ marginBottom: 8 }}>Enter your 16-digit card number</div>
      <div className="card-number-display">{chunks || "_ _ _ _"}<Cursor /></div>
      <div className="card-count-msg">{input.length}/16 digits entered</div>
      {error && <div className="screen-error">{error}</div>}
    </div>
  );
}

// ── PIN screen (shared for initial login and re-auth) ──────────────────────
function PinScreen({ length, error, onFaceAuth, faceStatus, title = "ENTER PIN", subtitle = "Enter your 4-digit PIN" }) {
  return (
    <div className="screen-inner">
      <div className="screen-title" style={title === "SESSION EXPIRED" ? { color: "#fc3" } : {}}>
        {title}
      </div>
      <div className="pin-dots">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`pin-dot${i < length ? " filled" : ""}`} />
        ))}
      </div>
      <div className="screen-msg">{subtitle}</div>
      {error && <div className="screen-error">{error}</div>}

      {/* ── Face ID option ── */}
      <div className="face-auth-divider"><span>OR</span></div>
      <button className="face-auth-btn" onClick={onFaceAuth}>
        {faceStatus === "scanning" && (
          <span className="face-scanning">
            <span className="face-scan-ring" />
            Scanning face…
          </span>
        )}
        {faceStatus === "success" && <span style={{ color: "#00ff88" }}>✔ Face Verified</span>}
        {faceStatus === "fail"    && <span style={{ color: "#ff4444" }}>✗ Face Not Recognised</span>}
        {faceStatus === "idle"    && <span>👤 Use Face ID</span>}
        {faceStatus === "no_model" && <span style={{ color: "#fc3" }}>⚠ Models not loaded</span>}
      </button>
    </div>
  );
}

// ── Menu screen ────────────────────────────────────────────────────────────
const MENU_ITEMS = ["Withdraw Cash", "Check Balance", "Mini Statement", "Spending Chart"];
function MenuScreen({ sel, onSelect }) {
  return (
    <div className="screen-inner">
      <div className="screen-title">SELECT SERVICE</div>
      {MENU_ITEMS.map((item, i) => (
        <div key={i} className="menu-item" onClick={() => onSelect(i)}>
          <div className={`menu-dot${i === sel ? " selected" : ""}`} />
          <div className={`menu-text${i === sel ? " selected" : ""}`}>{item}</div>
        </div>
      ))}
      <div className="screen-msg" style={{ fontSize: 9 }}>Use side buttons or tap</div>
    </div>
  );
}

// ── Withdraw screen ────────────────────────────────────────────────────────
function WithdrawScreen({ amount, balance, error, onQuick }) {
  const display = amount ? fmtINR(Number(amount)) : "₹ 0";
  return (
    <div className="screen-inner">
      <div className="screen-title">WITHDRAW CASH</div>
      <div className="screen-amount">{display}<Cursor /></div>
      <div className="withdraw-balance">Balance: {fmtINR(balance)}</div>
      <div className="quick-amts">
        {[500, 1000, 2000, 5000].map(v => (
          <button key={v} className="quick-btn" onClick={() => onQuick(v)}>₹{v}</button>
        ))}
      </div>
      {error && <div className="screen-error">{error}</div>}
    </div>
  );
}

// ── Balance screen ─────────────────────────────────────────────────────────
function BalanceScreen({ balance }) {
  return (
    <div className="screen-inner">
      <div className="screen-title">ACCOUNT BALANCE</div>
      <div className="screen-big">{fmtINR(balance)}</div>
      <div className="balance-label">Available Balance</div>
      <hr className="screen-divider" />
      <div className="screen-msg" style={{ fontSize: 10 }}>Press CANCEL to return</div>
    </div>
  );
}

// ── Transaction screen ─────────────────────────────────────────────────────
function TransactionScreen({ history }) {
  const recent = [...history].reverse().slice(0, 5);
  return (
    <div className="screen-inner">
      <div className="screen-title">MINI STATEMENT</div>
      {recent.map((t, i) => (
        <div key={i} className="txn-row">
          <span className="txn-date">{t.date}</span>
          <span className="txn-desc">{t.desc}</span>
          <span className={t.type === "credit" ? "txn-credit" : "txn-debit"}>
            {t.type === "credit" ? "+" : "-"}{fmtINR(t.amount)}
          </span>
        </div>
      ))}
      <div className="screen-msg" style={{ fontSize: 9 }}>CANCEL to go back</div>
    </div>
  );
}

// ── Spending bar chart screen ──────────────────────────────────────────────
function SpendingScreen({ history }) {
  const debits  = history.filter(t => t.type === "debit");
  const total   = debits.reduce((a, t) => a + t.amount, 0);
  const maxAmt  = Math.max(...debits.map(t => t.amount), 1);
  const COLORS  = ["#00ff88","#5dffa0","#3db870","#00cc6a","#7affb8"];

  return (
    <div className="screen-inner">
      <div className="screen-title">SPENDING CHART</div>

      {/* Bar chart */}
      <div className="chart-area">
        {debits.slice(-5).map((t, i) => {
          const heightPct = Math.round((t.amount / maxAmt) * 100);
          const color = COLORS[i % COLORS.length];
          return (
            <div key={i} className="chart-bar-col">
              <div className="chart-bar-label-top" style={{ color }}>
                {(t.amount / 1000).toFixed(1)}k
              </div>
              <div className="chart-bar-track">
                <div className="chart-bar-fill" style={{ height: `${heightPct}%`, background: color, boxShadow: `0 0 6px ${color}66` }} />
              </div>
              <div className="chart-bar-name">{t.desc.split(" ")[0].slice(0, 5)}</div>
            </div>
          );
        })}
      </div>

      <hr className="screen-divider" />

      {/* Detail list */}
      <div className="chart-details">
        {debits.slice(-5).map((t, i) => {
          const pct   = total ? Math.round((t.amount / total) * 100) : 0;
          const color = COLORS[i % COLORS.length];
          return (
            <div key={i} className="chart-detail-row">
              <div className="chart-detail-dot" style={{ background: color }} />
              <div className="chart-detail-name">{t.desc}</div>
              <div className="chart-detail-pct" style={{ color }}>{pct}%</div>
              <div className="chart-detail-amt">{fmtINR(t.amount)}</div>
            </div>
          );
        })}
      </div>

      <div className="spend-total">Total: {fmtINR(total)}</div>
    </div>
  );
}

// ── QR screen ──────────────────────────────────────────────────────────────
function QRScreen({ userId = "demo" }) {
   const canvasRef = useRef(null);
  const qrUrl = `https://atmsim-pip.vercel.app/upi?user=${userId}&amount=1000`;
    useEffect(() => {
    if (!canvasRef.current) return;

    QRCode.toCanvas(
      canvasRef.current,
      qrUrl,
      {
        width: 180,
        margin: 2,
      },
      (err) => {
        if (err) console.error(err);
      }
    );
  }, [qrUrl]);

   return (
    <div className="screen-inner">
      <div className="screen-title">SCAN TO PAY</div>

      <div className="qr-wrapper">
        <canvas
          ref={canvasRef}
          className="qr-canvas"
        />
      </div>

      <div className="qr-hint">Scan with any phone camera</div>
      <div className="qr-sub">Opens NAPPS Pay mobile page</div>
    </div>
  );
}

// ── Processing screen ──────────────────────────────────────────────────────
function ProcessingScreen() {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? "." : d + "."), 400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="screen-inner">
      <div className="screen-title">PROCESSING</div>
      <div className="processing-msg">Please wait{dots}</div>
      <div className="processing-sub">Authorizing transaction</div>
    </div>
  );
}

// ── Dispensing screen ──────────────────────────────────────────────────────
function DispensingScreen({ amount, balance }) {
  return (
    <div className="screen-inner">
      <div className="screen-approved">✔ APPROVED</div>
      <div className="screen-big">{fmtINR(amount)}</div>
      <div className="screen-collect">Please collect your cash</div>
      <hr className="screen-divider" />
      <div className="screen-msg" style={{ fontSize: 10 }}>New balance: {fmtINR(balance)}</div>
      <div className="dispensing-hint">Press ENTER or CANCEL to finish</div>
    </div>
  );
}

// ── Thank you screen ───────────────────────────────────────────────────────
function ThankYouScreen() {
  return (  
    <div className="screen-inner">
      <div className="bank-name">THANK YOU</div>
      <div className="bank-sub">Transaction complete</div>
      <div className="thankyou-msg">Have a great day!</div>
      <div className="thankyou-sub">Returning to home screen…</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN ATM COMPONENT
// All state + face-api logic lives HERE so setFaceStatus is always in scope
// ══════════════════════════════════════════════════════════════════════════
export default function ATMDashboard() {
  // ── state ────────────────────────────────────────────────────────────
  const [screen,       setScreen]       = useState("welcome");
  const [prevScreen,   setPrevScreen]   = useState("menu");
  const [cardInput,    setCardInput]    = useState("");
  const [pinInput,     setPinInput]     = useState("");
  const [amountInput,  setAmountInput]  = useState("");
  const [balance,      setBalance]      = useState(INIT_BALANCE);
  const [menuSel,      setMenuSel]      = useState(0);
  const [txnHistory,   setTxnHistory]   = useState(INITIAL_TXN);
  const [lastWithdraw, setLastWithdraw] = useState(0);
  const [errorMsg,     setErrorMsg]     = useState("");
  const [notes,        setNotes]        = useState([]);
  const [cardAnim,     setCardAnim]     = useState(false);
  const [loggedIn,     setLoggedIn]     = useState(false);
  const [authToken,    setAuthToken]    = useState(null);

  // ── face-api state (MUST be inside component for setFaceStatus to work) ──
  const [faceStatus,     setFaceStatus]     = useState("idle");
  // "idle" | "scanning" | "success" | "fail" | "no_model"

  const [faceModelsReady, setFaceModelsReady] = useState(false);

  // ── refs ──────────────────────────────────────────────────────────────
  const timeoutRef  = useRef(null);

  // ── Load face models on mount ─────────────────────────────────────────
  useEffect(() => {
    loadFaceModels().then(() => {
      setFaceModelsReady(faceModelsLoaded);
    });
  }, []);

  // ── 1-minute session timeout ──────────────────────────────────────────
  const resetTimer = useCallback(() => {
    if (!loggedIn) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setScreen(current => {
        setPrevScreen(current);
        return "reauth";
      });
      setPinInput("");
      setFaceStatus("idle");
    }, PIN_TIMEOUT_MS);
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn) resetTimer();
    return () => clearTimeout(timeoutRef.current);
  }, [loggedIn, resetTimer]);

  // Wrap interactions to reset the idle timer
  const withActivity = (fn) => (...args) => { resetTimer(); fn(...args); };

  // ── auto-advance screens ───────────────────────────────────────────────
  useEffect(() => {
    if (screen === "thank_you") {
      const t = setTimeout(() => {
        setScreen("welcome");
        setErrorMsg("");
        setLoggedIn(false);
        setAuthToken(null);
      }, 3000);
      return () => clearTimeout(t);
    }
    if (screen === "dispensing") {
      const nc = Math.min(4, Math.ceil(lastWithdraw / 500));
      const lp = ["10%", "28%", "48%", "68%"];
      setNotes(Array.from({ length: nc }, (_, i) => ({
        key:    Date.now() + i,
        amount: Math.round(lastWithdraw / nc / 100) * 100,
        delay:  i * 0.18,
        left:   lp[i],
      })));
      const t = setTimeout(() => setNotes([]), 2500);
      return () => clearTimeout(t);
    }
  }, [screen, lastWithdraw]);

  // ── PIN auto-confirm ───────────────────────────────────────────────────
  useEffect(() => {
    if ((screen === "pin_entry" || screen === "reauth") && pinInput.length === 4) {
      const t = setTimeout(async () => {
        // If we have a real auth token, verify against backend
        if (authToken) {
          try {
            const res = await fetch(`${API_BASE}/pin`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": authToken },
              body: JSON.stringify({ pin: pinInput }),
            });
            const data = await res.json();
            if (data.status === "ok") {
              handleAuthSuccess();
            } else {
              setErrorMsg("Incorrect PIN. Try again.");
              setPinInput("");
            }
            return;
          } catch {
            // Fall through to local check if backend unreachable
          }
        }
        // Local demo check
        if (pinInput === CORRECT_PIN) {
          handleAuthSuccess();
        } else {
          setErrorMsg("Incorrect PIN. Try again.");
          setPinInput("");
        }
      }, 350);
      return () => clearTimeout(t);
    }
  }, [pinInput, screen]); // eslint-disable-line

  // ── Auth success helper ────────────────────────────────────────────────
  const handleAuthSuccess = () => {
    if (screen === "reauth") {
      setScreen(prevScreen === "reauth" ? "menu" : prevScreen);
    } else {
      setScreen("menu");
      setMenuSel(0);
    }
    setLoggedIn(true);
    setErrorMsg("");
    setFaceStatus("idle");
  };

  // ══════════════════════════════════════════════════════════════════════
  // FACE AUTH — all logic inside the component, so setFaceStatus is valid
  // ══════════════════════════════════════════════════════════════════════
  const handleFaceAuth = useCallback(async () => {
    if (!faceModelsReady) {
      setFaceStatus("no_model");
      setTimeout(() => setFaceStatus("idle"), 2500);
      return;
    }

    setFaceStatus("scanning");

    // Create a hidden video element and start webcam
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });

      // Create video element (not added to DOM — faceapi can work with it offscreen)
      const video = document.createElement("video");
      video.srcObject = stream;
      video.autoplay  = true;
      video.muted     = true;
      video.width     = 320;
      video.height    = 240;

      // Wait for video to be ready
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
        setTimeout(reject, 8000); // 8s timeout
      });

      await video.play();

      // Give camera a moment to warm up (exposure, focus)
      await new Promise(r => setTimeout(r, 1200));

      // Detect face with landmarks and descriptor
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setFaceStatus("fail");
        setTimeout(() => setFaceStatus("idle"), 2500);
        return;
      }

      // ── Option A: Backend verification (recommended for production) ──
      if (authToken) {
        // Capture frame from video as base64 JPEG
        const captureCanvas = document.createElement("canvas");
        captureCanvas.width  = video.videoWidth  || 320;
        captureCanvas.height = video.videoHeight || 240;
        captureCanvas.getContext("2d").drawImage(video, 0, 0);
        const imageBase64 = captureCanvas.toDataURL("image/jpeg", 0.85);

        try {
          const res = await fetch(`${API_BASE}/face-verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": authToken },
            body: JSON.stringify({ image: imageBase64 }),
          });
          const data = await res.json();

          if (data.status === "ok") {
            setFaceStatus("success");
            setTimeout(() => handleAuthSuccess(), 600);
          } else if (data.status === "no_face_enrolled") {
            setFaceStatus("fail");
            setErrorMsg("No face enrolled. Use PIN.");
            setTimeout(() => setFaceStatus("idle"), 2500);
          } else {
            setFaceStatus("fail");
            setTimeout(() => setFaceStatus("idle"), 2500);
          }
          return;
        } catch {
          // Backend unreachable — fall through to local descriptor check below
        }
      }

      // ── Option B: Local descriptor comparison (demo / offline mode) ──
      // In production you'd compare against a stored descriptor fetched from backend.
      // For demo: if a face is detected at all, we accept it.
      const storedDescriptor = null; // Replace with real stored descriptor

      if (storedDescriptor) {
        const distance = faceapi.euclideanDistance(storedDescriptor, detection.descriptor);
        if (distance < 0.5) {
          setFaceStatus("success");
          setTimeout(() => handleAuthSuccess(), 600);
        } else {
          setFaceStatus("fail");
          setTimeout(() => setFaceStatus("idle"), 2500);
        }
      } else {
        // Demo mode: any detected face passes
        console.warn("No stored descriptor — running in demo mode, any face will pass.");
        setFaceStatus("success");
        setTimeout(() => handleAuthSuccess(), 600);
      }

    } catch (err) {
      console.error("Face auth error:", err);
      // Handle common errors
      if (err.name === "NotAllowedError") {
        setErrorMsg("Camera permission denied");
      } else if (err.name === "NotFoundError") {
        setErrorMsg("No camera found");
      }
      setFaceStatus("fail");
      setTimeout(() => setFaceStatus("idle"), 2500);
    } finally {
      // Always stop the camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  }, [faceModelsReady, authToken, screen, prevScreen]); // eslint-disable-line

  // ── Key handler ────────────────────────────────────────────────────────
  const handleKey = withActivity((key) => {
    setErrorMsg("");
    if (screen === "welcome") {
      setCardInput(""); setCardAnim(true);
      setTimeout(() => setCardAnim(false), 700);
      setScreen("card_entry"); return;
    }
    if (screen === "card_entry") {
      if (key === "cancel" || key === "back") { setScreen("welcome"); return; }
      if (key === "del")   { setCardInput(p => p.slice(0, -1)); return; }
      if (key === "clear") { setCardInput(""); return; }
      if (key === "enter") {
        if (cardInput === CARD_NO) {
          setScreen("pin_entry"); setPinInput(""); setFaceStatus("idle");
        } else {
          setErrorMsg("Invalid card number. Try again.");
        }
        return;
      }
      if (/\d/.test(key) && cardInput.length < 16) setCardInput(p => p + key);
      return;
    }
    if (screen === "pin_entry" || screen === "reauth") {
      if (key === "cancel" || key === "back") {
        if (screen === "reauth") { setScreen("welcome"); setLoggedIn(false); setAuthToken(null); }
        else setScreen("card_entry");
        setPinInput(""); return;
      }
      if (key === "del")   { setPinInput(p => p.slice(0, -1)); return; }
      if (key === "clear") { setPinInput(""); return; }
      if (key === "enter") return; // auto-handled by useEffect
      if (/\d/.test(key) && pinInput.length < 4) setPinInput(p => p + key);
      return;
    }
    if (screen === "menu") {
      if (key === "cancel") { setScreen("welcome"); setLoggedIn(false); setAuthToken(null); return; }
      if (key === "enter")  { selectMenu(menuSel); return; }
      return;
    }
    if (screen === "withdraw") {
      if (key === "cancel" || key === "back") { setScreen("menu"); setAmountInput(""); return; }
      if (key === "del")   { setAmountInput(p => p.slice(0, -1)); return; }
      if (key === "clear") { setAmountInput(""); return; }
      if (key === "enter") { doWithdraw(); return; }
      if (/\d/.test(key) && amountInput.length < 7) setAmountInput(p => p + key);
      return;
    }
    if (["balance", "transactions", "spending", "qr"].includes(screen)) {
      if (key === "cancel" || key === "back") { setScreen("menu"); return; }
      return;
    }
    if (screen === "dispensing") {
      if (key === "cancel" || key === "enter") { setScreen("thank_you"); return; }
      return;
    }
  });

  const selectMenu = withActivity((i) => {
    setMenuSel(i);
    const map = ["withdraw", "balance", "transactions", "spending"];
    setScreen(map[i] || "balance");
    if (map[i] === "withdraw") setAmountInput("");
  });

  const doWithdraw = async () => {
    const amt = Number(amountInput);
    if (!amt || amt <= 0) { setErrorMsg("Enter a valid amount"); return; }
    if (amt > balance)    { setErrorMsg("Insufficient balance!"); return; }
    if (amt % 100 !== 0)  { setErrorMsg("Amount must be multiple of ₹100"); return; }

    setScreen("processing");

    // Try real backend first
    if (authToken) {
      try {
        const res = await fetch(`${API_BASE}/withdraw`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": authToken },
          body: JSON.stringify({ amount: amt }),
        });
        const data = await res.json();
        if (data.new_balance !== undefined) {
          setBalance(data.new_balance);
          setLastWithdraw(amt);
          setTxnHistory(prev => [...prev, { id: Date.now(), type: "debit", desc: "ATM Withdrawal", amount: amt, date: "19 Apr" }]);
          setScreen("dispensing");
          return;
        }
      } catch {
        // Fall through to local simulation
      }
    }

    // Local demo simulation
    setTimeout(() => {
      setBalance(b => b - amt);
      setLastWithdraw(amt);
      setTxnHistory(prev => [...prev, { id: Date.now(), type: "debit", desc: "ATM Withdrawal", amount: amt, date: "19 Apr" }]);
      setScreen("dispensing");
    }, 1800);
  };

  const handleSideBtn = withActivity((i) => {
    if (screen === "welcome") {
      setCardInput(""); setCardAnim(true);
      setTimeout(() => setCardAnim(false), 700);
      setScreen("card_entry"); return;
    }
    if (screen === "menu") selectMenu(i);
  });

  const handleNavBtn = withActivity((type) => {
    if (!["menu","balance","transactions","spending","dispensing","withdraw"].includes(screen)) return;
    const map = { txn: "transactions", spend: "spending", qr: "qr" };
    if (map[type]) setScreen(map[type]);
  });

  // ── Screen renderer ────────────────────────────────────────────────────
  const renderScreen = () => {
    switch (screen) {
      case "welcome":
        return <WelcomeScreen />;
      case "card_entry":
        return <CardEntryScreen input={cardInput} error={errorMsg} />;
      case "pin_entry":
        return (
          <PinScreen
            length={pinInput.length}
            error={errorMsg}
            onFaceAuth={handleFaceAuth}
            faceStatus={faceStatus}
          />
        );
      case "reauth":
        return (
          <PinScreen
            length={pinInput.length}
            error={errorMsg}
            onFaceAuth={handleFaceAuth}
            faceStatus={faceStatus}
            title="SESSION EXPIRED"
            subtitle="Re-enter PIN to continue"
          />
        );
      case "menu":
        return <MenuScreen sel={menuSel} onSelect={selectMenu} />;
      case "withdraw":
        return (
          <WithdrawScreen
            amount={amountInput}
            balance={balance}
            error={errorMsg}
            onQuick={v => { setAmountInput(String(v)); setErrorMsg(""); }}
          />
        );
      case "balance":
        return <BalanceScreen balance={balance} />;
      case "transactions":
        return <TransactionScreen history={txnHistory} />;
      case "spending":
        return <SpendingScreen history={txnHistory} />;
      case "qr":
        return <QRScreen userId="demo" />;
      case "processing":
        return <ProcessingScreen />;
      case "dispensing":
        return <DispensingScreen amount={lastWithdraw} balance={balance} />;
      case "thank_you":
        return <ThankYouScreen />;
      default:
        return null;
    }
  };

  const KEYS       = ["1","2","3","4","5","6","7","8","9","clear","0","del"];
  const KEY_LABELS = { clear: "CLR", del: "⌫" };

  return (
    <div className="atm-page">
      <div className="atm-body">
        <div className="atm-brand-label">NAPPS BANK ATM</div>

        {/* Screen + side buttons */}
        <div className="mid-row">
          <div className="screen-col">
            <div className="screen-bezel">
              <div className="screen">
                <div className="screen-scanlines" />
                {renderScreen()}
              </div>
            </div>
          </div>
          <div className="side-btn-col">
            {[0,1,2,3,4].map(i => (
              <button
                key={i}
                className={`side-btn${screen === "menu" && menuSel === i ? " active" : ""}`}
                onClick={() => handleSideBtn(i)}
              />
            ))}
          </div>
        </div>

        {/* Card slot */}
        <div className="card-slot-area">
          <div className="card-slot">
            {cardAnim && <div className="card-anim" />}
          </div>
          <div className="slot-label">CARD SLOT</div>
        </div>
        

        {/* Keypad */}
        <div className="keypad">
          <div className="key-grid">
            {KEYS.map(k => (
              <button
                key={k}
                className={`key${k === "clear" ? " key-yellow" : k === "del" ? " key-red" : ""}`}
                onClick={() => handleKey(k)}
              >
                {KEY_LABELS[k] || k}
              </button>
            ))}
          </div>
          <div className="func-row">
            <button className="cancel-b"    onClick={() => handleKey("cancel")}>CANCEL</button>
            <button className="back-b" onClick={() => handleKey("back")}>BACK</button>
            <button className="enter-b"  onClick={() => handleKey("enter")}>ENTER</button>
          </div>
        </div>
        {/* Cash dispenser */}
        <div className="cash-slot-area">
          <div className="cash-slot">
            {notes.map(n => <CashNote key={n.key} amount={n.amount} delay={n.delay} left={n.left} />)}
          </div>
          <div className="slot-label">CASH DISPENSER</div>
        </div>

        {/* Nav buttons */}
        <div className="nav-btns">
          <button className="nav-btn" onClick={() => handleNavBtn("txn")}>Recent Txns</button>
          <button className="nav-btn" onClick={() => handleNavBtn("spend")}>Spending</button>
          <button className="nav-btn nav-btn-qr" onClick={() => handleNavBtn("qr")}>📱 QR Pay</button>
        </div>
      </div>
    </div>
  );
}